import { DialogM } from "@common/components/alert/dialog/Dialog"
import LiveParticipant from "@common/liveMode/liveV3/LiveParticipant"
import { theme } from "@common/theme/theme"
import { DimensionsContext, IS_WEB } from "@common/utils/mobileUtils"
import {
    setFocusScreenShare,
    setLeftPanelCollapsed,
    setRightPanelCollapsed,
} from "@data/redux/actions/liveMode"
import { findItemInListWithId } from "@data/utility/dataUtils"
import { useNavigation } from "@react-navigation/native"
import moment from "moment/moment"
import {
    useIsSoundMode,
} from "@common/hooks/livekit/useParticipantState"
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { Dimensions, Pressable, ScrollView, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect } from "react-redux"
import TodoModal from "../../../student/activity/todo/TodoModal"
import SchoolAssessmentsScreen from "../../../student/schoolAssessments/SchoolAssessmentsScreen"
import SchoolAssessmentsIntroModal from "../../../student/schoolAssessments/SchoolAssessmentsIntroModal"
import NewStudentsListItem from "../../../tutor/students/NewStudentsListItem"

const TutorFocusLayoutM = (props) => {
    const {
        participants,
        onParticipantClick,
        onTrackClick,
        setLiveChatUserId,
        setAgentCheckpointsUserId,
        pinnedParticipantId = null,
        pinnedTrackSid = null,
        liveSessionType,
        tracksMap,
        style = {},
    } = props

    const dimensions = useContext(DimensionsContext)

    const navigation = useNavigation()

    const pinnedParticipant = participants?.find(
        (x) => x.id === pinnedParticipantId
    )

    const finishedActivities = pinnedParticipant?.activities
    const mostRecentlyLockedActivity = pinnedParticipant?.todo_activities?.sort(
        (a, b) => moment(b?.locked_at).diff(moment(a?.locked_at))
    )?.[0]
    // Prioritize the student's current activity from LiveKit attributes if available
    const studentCurrentActivityId = pinnedParticipantId
        ? props.currentStudentActivity?.[pinnedParticipantId]
        : null
    const defaultSelectedActivityId =
        studentCurrentActivityId ||
        (pinnedParticipant?.activities?.[0]?.id ??
        mostRecentlyLockedActivity?.id ??
        null)
    const defaultSelectedActivity = defaultSelectedActivityId
        ? pinnedParticipant?.todo_activities
              ?.concat(finishedActivities ?? [])
              ?.find((todo) => todo?.id === defaultSelectedActivityId)
        : null

    const [selectedActivityId, setSelectedActivityId] = useState(
        defaultSelectedActivity?.id ?? null
    )

    // State for showing assessments view in center panel
    const [showAssessmentsView, setShowAssessmentsView] = useState(false)

    const isFocusScreenShare = props.isFocusScreenShare
    const leftPanelCollapsed = props.leftPanelCollapsed
    const rightPanelCollapsed = props.rightPanelCollapsed

    useEffect(() => {
        if (pinnedParticipantId) {
            const newActivityId = defaultSelectedActivity?.id ?? null
            if (newActivityId || !isFocusScreenShare) {
                setSelectedActivityId(newActivityId)
            }
            setShowAssessmentsView(false) // Reset when switching students
        }
        // Also re-run when studentCurrentActivityId changes so an external
        // "open this activity" request (e.g. the events panel's "Voir le rendu")
        // selects the right activity even when the student is already pinned.
    }, [pinnedParticipantId, studentCurrentActivityId])

    // Explicit focus request from the events-panel CTA. Unlike the effect above
    // (which only re-fires when pinnedParticipantId/studentCurrentActivityId
    // change), this honours every click via a bumping requestId — required for
    // auto-mode, where the student is already ON the chosen activity, so the
    // passive LiveKit attribute sync already set currentStudentActivity to it
    // and the CTA would otherwise be a no-op. Waits until the requested student
    // is pinned (the CTA pins them in the same interaction). selectedActivity's
    // useMemo resolves the énoncé once the activity lands in todo_activities.
    const handledFocusRequestRef = useRef(0)
    useEffect(() => {
        const req = props.studentActivityFocusRequest
        if (!req || req.requestId === handledFocusRequestRef.current) return
        if (pinnedParticipantId !== req.studentId) return
        handledFocusRequestRef.current = req.requestId
        setShowAssessmentsView(false)
        setSelectedActivityId(req.activityId)
    }, [props.studentActivityFocusRequest, pinnedParticipantId])

    // Show confirmation dialog when tutor tries to navigate away during presentation
    const confirmInterruptPresentation = useCallback((action) => {
        if (!isFocusScreenShare || !IS_WEB) {
            action()
            return
        }
        DialogM.show({
            text1: "Interrompre la présentation ?",
            text2: "Le partage d'écran sera arrêté si tu quittes cette vue.",
            buttonText1: "Interrompre",
            buttonColor1: theme.colors.error,
            onPress: () => {
                props.setFocusScreenShare(false)
                setTimeout(() => action(), 300)
            },
            buttonText2: "Rester",
        })
    }, [isFocusScreenShare])

    // Handler to show assessments view (clears activity selection)
    const handleShowAssessments = () => {
        confirmInterruptPresentation(() => {
            setSelectedActivityId(null)
            setShowAssessmentsView(true)
        })
    }

    // Handler for selecting an activity (clears assessments view)
    const handleSelectActivity = (activityId) => {
        setShowAssessmentsView(false)
        setSelectedActivityId(activityId)
    }

    // On mobile: collapse both panels when presentation starts (maximize content area)
    // On all platforms: restore panels when presentation stops
    useEffect(() => {
        if (isFocusScreenShare && !IS_WEB) {
            props.setLeftPanelCollapsed(true)
            props.setRightPanelCollapsed(true)
        }
        if (!isFocusScreenShare) {
            props.setLeftPanelCollapsed(false)
            props.setRightPanelCollapsed(false)
        }
    }, [isFocusScreenShare])

    const collapsedLeftWidth = 40
    const collapsedRightWidth = 80
    const hasLeftPanel = dimensions?.width > 700

    const isLeftCollapsed = isFocusScreenShare && leftPanelCollapsed
    const isRightCollapsed = isFocusScreenShare && rightPanelCollapsed

    const rightPanelWidth = isRightCollapsed
        ? collapsedRightWidth
        : dimensions?.width > 700
            ? Dimensions.get("window").width / 8
            : 20
    const leftPanelWidth = isLeftCollapsed
        ? (hasLeftPanel ? collapsedLeftWidth : 0)
        : (hasLeftPanel ? Dimensions.get("window").width / 5 : 0)
    const centerPanelWidth =
        Dimensions.get("window").width - rightPanelWidth - leftPanelWidth

    const selectedActivity = useMemo(() => {
        if (!selectedActivityId) return null
        return pinnedParticipant?.todo_activities
            ?.concat(finishedActivities ?? [])
            ?.find((todo) => todo?.id === selectedActivityId) ?? {
            id: selectedActivityId,
        }
    }, [selectedActivityId, pinnedParticipant?.todo_activities, finishedActivities])

    const onTodoExit = useCallback(() => {
        if (!isFocusScreenShare || !IS_WEB) setSelectedActivityId(null)
    }, [isFocusScreenShare])

    const onTodoRequestClose = useCallback(() => {
        confirmInterruptPresentation(() => setSelectedActivityId(null))
    }, [confirmInterruptPresentation])

    const schoolSubject = useMemo(
        () => findItemInListWithId(selectedActivity?.school_subject_id, props.schoolSubjects),
        [selectedActivity?.school_subject_id, props.schoolSubjects]
    )

    const isSoundOn = useIsSoundMode(pinnedParticipant?.id)

    const collapseButtonStyle = {
        width: 20,
        height: 40,
        borderRadius: 10,
        backgroundColor: theme.colors.white,
        alignItems: "center",
        justifyContent: "center",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.15,
        shadowRadius: 2,
        elevation: 2,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
    }

    return (
        <View
            style={{
                flex: 1,
                flexDirection: dimensions?.width <= 700 ? "column" : "row",
                ...style,
            }}
        >
            {/* Focus layout implementation will go here */}
            <View
                style={{
                    flex: 1,
                    alignItems: "stretch",
                }}
            >
                <View
                    style={{
                        flex: 1,
                        flexDirection: "row",
                    }}
                >
                    {hasLeftPanel && isFocusScreenShare && leftPanelCollapsed ? (
                        <Pressable
                            onPress={() => props.setLeftPanelCollapsed(false)}
                            style={{
                                width: collapsedLeftWidth,
                                backgroundColor: theme.colors.lighter,
                                alignItems: "center",
                                justifyContent: "center",
                                borderRightWidth: 1,
                                borderRightColor: theme.colors.lightMore,
                            }}
                        >
                            <View style={collapseButtonStyle}>
                                <FontAwesome5
                                    name="chevron-right"
                                    size={10}
                                    color={theme.colors.primary}
                                />
                            </View>
                        </Pressable>
                    ) : hasLeftPanel && (
                        <View
                            style={{
                                paddingTop: 5,
                                width: leftPanelWidth,
                                maxWidth: leftPanelWidth,
                                backgroundColor: theme.colors.lighter,
                                paddingHorizontal: 0,
                            }}
                        >
                            {isFocusScreenShare && (
                                <Pressable
                                    onPress={() => props.setLeftPanelCollapsed(true)}
                                    style={{
                                        position: "absolute",
                                        top: "50%",
                                        right: -10,
                                        transform: [{ translateY: -20 }],
                                        zIndex: 10,
                                        ...collapseButtonStyle,
                                    }}
                                >
                                    <FontAwesome5
                                        name="chevron-left"
                                        size={10}
                                        color={theme.colors.primary}
                                    />
                                </Pressable>
                            )}
                            <NewStudentsListItem
                                student={pinnedParticipant}
                                isTutor={pinnedParticipant?.is_tutor}
                                index={pinnedParticipant?.id}
                                studentsNumber={5}
                                byChapter={false}
                                soundMode={isSoundOn}
                                withTodo
                                additionalActions={null}
                                isLiveMode
                                noMessageButton
                                onSelectActivity={handleSelectActivity}
                                onShowAssessments={handleShowAssessments}
                                selectedActivityId={selectedActivityId}
                                isAssessmentsViewActive={showAssessmentsView}
                                onBeforeNavigate={confirmInterruptPresentation}
                            />
                        </View>
                    )}
                    <View
                        style={{
                            flex: 1,
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {showAssessmentsView ? (
                            // Assessments view
                            <View
                                style={{
                                    flex: 1,
                                    borderRadius: 10,
                                    borderWidth: 1,
                                    borderColor: theme.colors.lightMore,
                                    alignSelf: "center",
                                    alignItems: "stretch",
                                    marginTop: 5,
                                    marginBottom: 10,
                                    marginLeft: dimensions?.width > 700 ? 5 : 0,
                                    backgroundColor: theme.colors.white,
                                    overflow: "hidden",
                                }}
                            >
                                <SchoolAssessmentsScreen
                                    noModal
                                    width={centerPanelWidth - 10}
                                    studentId={pinnedParticipant?.id}
                                    student={pinnedParticipant}
                                    onClose={() =>
                                        setShowAssessmentsView(false)
                                    }
                                    schoolSubjects={props.schoolSubjects}
                                />
                            </View>
                        ) : !selectedActivityId ? (
                            dimensions?.width > 700 ? (
                                <LiveParticipant
                                    user={pinnedParticipant}
                                    userTracks={tracksMap?.[pinnedParticipant?.id] || []}
                                    style={{
                                        width: centerPanelWidth - 10,
                                        flex: 1,
                                        marginTop: 5,
                                        marginBottom: 10,
                                    }}
                                    containerStyle={{
                                        borderRadius: 10,
                                    }}
                                    noAdditionalActions
                                    setLiveChatUserId={setLiveChatUserId}
                                    setAgentCheckpointsUserId={
                                        setAgentCheckpointsUserId
                                    }
                                    showPopUp={false}
                                />
                            ) : (
                                <NewStudentsListItem
                                    student={pinnedParticipant}
                                    isTutor={pinnedParticipant?.is_tutor}
                                    index={pinnedParticipant?.id}
                                    studentsNumber={5}
                                    byChapter={false}
                                    soundMode={isSoundOn}
                                    withTodo
                                    additionalActions={null}
                                    isLiveMode
                                    noMessageButton
                                    onSelectActivity={handleSelectActivity}
                                    onShowAssessments={handleShowAssessments}
                                    selectedActivityId={selectedActivityId}
                                    isAssessmentsViewActive={
                                        showAssessmentsView
                                    }
                                    onBeforeNavigate={confirmInterruptPresentation}
                                    style={{ marginLeft: 10 }}
                                />
                            )
                        ) : (
                            <View
                                style={{
                                    flex: 1,
                                    borderRadius: 10,
                                    borderWidth: 1,
                                    borderColor: theme.colors.lightMore,
                                    alignSelf: "center",
                                    alignItems: "stretch",
                                    marginTop: 5,
                                    marginBottom: 10,
                                    marginLeft: dimensions?.width > 700 ? 5 : 0,
                                    backgroundColor: theme.colors.white,
                                }}
                            >
                                <TodoModal
                                    item={selectedActivity}
                                    navigation={navigation}
                                    onExit={onTodoExit}
                                    onRequestClose={onTodoRequestClose}
                                    width={centerPanelWidth}
                                    studentId={pinnedParticipant?.id}
                                    student={pinnedParticipant}
                                    schoolSubject={schoolSubject}
                                    noModal
                                />
                            </View>
                        )}
                    </View>
                </View>
            </View>
            <View style={{ position: "relative", width: dimensions?.width <= 700 ? undefined : rightPanelWidth }}>
                {isFocusScreenShare && dimensions?.width > 700 && (
                    <Pressable
                        onPress={() => isRightCollapsed
                            ? props.setRightPanelCollapsed(false)
                            : props.setRightPanelCollapsed(true)
                        }
                        style={{
                            position: "absolute",
                            top: "50%",
                            left: -10,
                            transform: [{ translateY: -20 }],
                            zIndex: 10,
                            ...collapseButtonStyle,
                        }}
                    >
                        <FontAwesome5
                            name={isRightCollapsed ? "chevron-left" : "chevron-right"}
                            size={10}
                            color={theme.colors.primary}
                        />
                    </Pressable>
                )}
                <ScrollView
                    style={{
                        width: dimensions?.width <= 700 ? undefined : rightPanelWidth,
                        maxHeight: dimensions?.width <= 700 ? 180 : null,
                        borderLeftWidth: isRightCollapsed ? 1 : 0,
                        borderLeftColor: theme.colors.lightMore,
                    }}
                    contentContainerStyle={{
                        gap: isRightCollapsed ? 6 : 10,
                        alignItems: "center",
                        paddingTop: 5,
                        paddingBottom: dimensions?.width <= 700 ? 0 : 160,
                        paddingLeft: dimensions?.width <= 700 ? 10 : 0,
                        paddingRight: dimensions?.width <= 700 ? 140 : 0,
                    }}
                    horizontal={dimensions?.width <= 700}
                >
                    {participants?.map((participant, index) =>
                        isRightCollapsed && dimensions?.width > 700 ? (
                            <LiveParticipant
                                key={participant.id}
                                user={participant}
                                userTracks={tracksMap?.[participant.id] || []}
                                style={{
                                    width: collapsedRightWidth - 10,
                                    height: collapsedRightWidth - 10,
                                }}
                                containerStyle={{
                                    borderRadius: 10,
                                }}
                                isSelected={participant.id === pinnedParticipantId}
                                hideTile={
                                    participant.id === pinnedParticipantId &&
                                    !selectedActivityId
                                }
                                hiddenTrackSid={pinnedTrackSid}
                                onParticipantClick={() => {
                                    onParticipantClick(participant.id)
                                    if (!isFocusScreenShare) setSelectedActivityId(null)
                                }}
                                onTrackClick={onTrackClick}
                                setLiveChatUserId={setLiveChatUserId}
                                setAgentCheckpointsUserId={setAgentCheckpointsUserId}
                                noAdditionalActions
                                compact
                                index={index}
                                showPopUp={true}
                            />
                        ) : (
                            <LiveParticipant
                                key={participant.id}
                                user={participant}
                                userTracks={tracksMap?.[participant.id] || []}
                                style={{
                                    width:
                                        dimensions?.width <= 700
                                            ? 120
                                            : rightPanelWidth - 20,
                                }}
                                containerStyle={{
                                    borderRadius: 15,
                                }}
                                isSelected={participant.id === pinnedParticipantId}
                                hideTile={
                                    participant.id === pinnedParticipantId &&
                                    !selectedActivityId
                                }
                                hiddenTrackSid={pinnedTrackSid}
                                onParticipantClick={() => {
                                    onParticipantClick(participant.id)
                                    if (!isFocusScreenShare) setSelectedActivityId(null)
                                }}
                                onTrackClick={onTrackClick}
                                setLiveChatUserId={setLiveChatUserId}
                                setAgentCheckpointsUserId={setAgentCheckpointsUserId}
                                index={index}
                                showPopUp={true}
                            />
                        )
                    )}
                </ScrollView>
            </View>

            {/* Introduction modal for school assessments feature */}
            {/* <SchoolAssessmentsIntroModal /> */}
        </View>
    )
}

const mapStateToProps = (state) => {
    return {
        schoolSubjects: state.data.staticData?.school_subjects,
        currentStudentActivity: state.liveMode?.currentStudentActivity,
        studentActivityFocusRequest: state.liveMode?.studentActivityFocusRequest,
        leftPanelCollapsed: state.liveMode?.leftPanelCollapsed,
        rightPanelCollapsed: state.liveMode?.rightPanelCollapsed,
        isFocusScreenShare: state.liveMode?.isFocusScreenShare,
    }
}

const mapDispatchToProps = {
    setLeftPanelCollapsed,
    setRightPanelCollapsed,
    setFocusScreenShare,
}
export default connect(mapStateToProps, mapDispatchToProps)(TutorFocusLayoutM)
